import React, { useRef, useState } from 'react';
import { Toast, ValidatingInput, ValidationForm, ValidationSummary } from '@ygtechnologies/bygr-frontend-component-library';
import { faPlus, faXmark } from '@fortawesome/pro-light-svg-icons';
import { AlertBanner, Button, ButtonBar, Dialog, Select } from '@ygtechnologies/rubus-ui';
import { MacGyverAccessLevel } from '@/services/auth';
import { createMacGyverUser } from '@/services/macgyverUsers';
import { extractErrorMessage } from '@/utils/ajax';
import { accessLevelOptions } from './shared';
import theme from './MacGyverUsersBulkAddModal.module.scss';

interface MacGyverUsersBulkAddModalProps {
    isOpen: boolean;
    toggle(): void;
    onSave(): void;
}

interface BulkRow {
    /** Stable React key, independent of position (rows are added/removed freely). */
    key: number;
    email: string;
    accessLevel: MacGyverAccessLevel;
    /** Error from the last submit attempt, cleared as soon as the row is edited. */
    error: string | null;
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Must stay module-level: ValidatingInput re-registers its rules whenever this array identity changes.
const emailValidations = [
    {
        isValid: (value: string) => !value?.trim() || emailPattern.test(value.trim()),
        message: () => 'Enter a valid email address.',
        summaryMessage: ({ field }: { field?: string }) => `${field} is not a valid email address.`,
    },
];

const levelByToken: Record<string, MacGyverAccessLevel> = {
    standard: 'Standard',
    advanced: 'Advanced',
    admin: 'Admin',
};

interface ParsedRow {
    email: string;
    accessLevel: MacGyverAccessLevel;
}

/**
 * Splits pasted text into rows, one line at a time. Within a line, whitespace, commas and semicolons
 * all separate tokens, so newline-, comma- and tab-separated lists work. A token that is exactly an
 * access level name applies to the email before it (so `user@example.com, Advanced` works).
 *
 * Tokens without an `@` can't be emails. Next to a recognised email they're just the display name of
 * a `Name <user@example.com>` paste, so they're dropped quietly; but a line nothing was understood
 * from is returned in `ignored` so the caller can say so instead of silently swallowing it.
 */
function parseRows(text: string): { rows: ParsedRow[]; ignored: string[] } {
    const rows: ParsedRow[] = [];
    const ignored: string[] = [];
    const seen = new Set<string>();

    for (const rawLine of text.split(/[\n\r]+/)) {
        const line = rawLine.trim();
        if (!line) {
            continue;
        }

        let understoodSomething = false;

        for (const rawToken of line.split(/[\s,;]+/)) {
            const token = rawToken.trim().replace(/^[<"']+/, '').replace(/[>"',.]+$/, '');
            if (!token) {
                continue;
            }

            const level = levelByToken[token.toLowerCase()];
            if (level) {
                if (rows.length > 0) {
                    rows[rows.length - 1].accessLevel = level;
                }
                understoodSomething = true;
                continue;
            }

            if (!token.includes('@')) {
                continue; // display name or noise — judged per line below
            }

            understoodSomething = true;
            if (!seen.has(token.toLowerCase())) {
                seen.add(token.toLowerCase());
                rows.push({ email: token, accessLevel: 'Standard' });
            }
        }

        if (!understoodSomething) {
            ignored.push(line);
        }
    }

    return { rows, ignored };
}

/** Human-readable note about lines the parser found no email in, or null when there were none. */
function describeIgnored(ignored: string[]): string | null {
    if (ignored.length === 0) {
        return null;
    }

    if (ignored.length === 1) {
        return `Ignored “${ignored[0]}” — no email address in it.`;
    }

    const shown = ignored.slice(0, 5).map((line) => `“${line}”`).join(', ');
    const rest = ignored.length - 5;

    return `Ignored ${ignored.length} lines with no email address in them: ${shown}${rest > 0 ? ` and ${rest} more` : ''}`;
}

const MacGyverUsersBulkAddModal: React.FC<MacGyverUsersBulkAddModalProps> = ({ isOpen, toggle, onSave }) => {
    const [rows, setRows] = useState<BulkRow[]>([]);
    const [pasteText, setPasteText] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [progress, setProgress] = useState(0);
    const [summary, setSummary] = useState<string | null>(null);
    const [ignoredNote, setIgnoredNote] = useState<string | null>(null);
    const nextKey = useRef(1);

    // Reset state each time the modal opens (the component instance is reused).
    const handleOpen = () => {
        setRows([]);
        setPasteText('');
        setProgress(0);
        setSummary(null);
        setIgnoredNote(null);
    };

    /** Appends `parsed` to `prev`, skipping emails already listed (case-insensitive). */
    const appendParsed = (prev: BulkRow[], parsed: ParsedRow[]): BulkRow[] => {
        const listed = new Set(prev.map((r) => r.email.trim().toLowerCase()));
        const added: BulkRow[] = [];

        for (const row of parsed) {
            if (listed.has(row.email.toLowerCase())) {
                continue;
            }
            listed.add(row.email.toLowerCase());
            added.push({ key: nextKey.current++, email: row.email, accessLevel: row.accessLevel, error: null });
        }

        return added.length > 0 ? [...prev, ...added] : prev;
    };

    const absorbPasteText = (text: string) => {
        const { rows: parsed, ignored } = parseRows(text);
        setRows((prev) => appendParsed(prev, parsed));
        setIgnoredNote(describeIgnored(ignored));
        setPasteText('');
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const pasted = e.clipboardData.getData('text');
        if (!pasted) {
            return;
        }
        e.preventDefault();
        absorbPasteText(`${pasteText}\n${pasted}`);
    };

    const handlePasteTextChange = (value: string) => {
        // Typing a deliberate separator (usually Enter) turns what came before it into rows right
        // away. Spaces are excluded here — while typing they're more likely a slip than a separator.
        if (/[\n\r,;]/.test(value)) {
            absorbPasteText(value);
        } else {
            setPasteText(value);
        }
    };

    const updateRow = (key: number, changes: Partial<BulkRow>) =>
        setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...changes, error: null } : row)));

    const removeRow = (key: number) => setRows((prev) => prev.filter((row) => row.key !== key));

    const addEmptyRow = () =>
        setRows((prev) => [...prev, { key: nextKey.current++, email: '', accessLevel: 'Standard', error: null }]);

    const handleSubmit = async () => {
        // Anything still sitting in the paste box becomes rows first, so it goes through validation on
        // the next submit rather than skipping it — and so ignored entries get reported, not swallowed.
        const { rows: parsed, ignored } = parseRows(pasteText);
        const merged = appendParsed(rows, parsed);
        if (merged !== rows || ignored.length > 0) {
            setRows(merged);
            setIgnoredNote(describeIgnored(ignored));
            setPasteText('');
            return;
        }

        setIsSaving(true);
        setProgress(0);
        setSummary(null);
        setIgnoredNote(null);

        const failed: BulkRow[] = [];
        let created = 0;

        for (const row of rows) {
            try {
                await createMacGyverUser({ email: row.email.trim(), accessLevel: row.accessLevel, isActive: true });
                created++;
            } catch (e) {
                failed.push({ ...row, error: (await extractErrorMessage(e)) ?? 'Something went wrong' });
            }
            setProgress((p) => p + 1);
        }

        setIsSaving(false);
        setProgress(0);

        if (created > 0) {
            onSave();
        }

        if (failed.length === 0) {
            Toast.success(created === 1 ? '1 user added' : `${created} users added`);
            toggle();
            return;
        }

        // Keep the failures listed so they can be fixed and submitted again.
        setRows(failed);
        setSummary(
            `${created} of ${created + failed.length} users added. The rows below were skipped — fix them and add again, or close.`,
        );
    };

    let submitLabel = rows.length === 1 ? 'Add 1 user' : `Add ${rows.length} users`;
    if (rows.length === 0) {
        submitLabel = 'Add users';
    }
    if (isSaving) {
        submitLabel = `Adding ${progress}/${rows.length}…`;
    }

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && toggle()} onOpen={handleOpen}>
            <Dialog.Content contentWidth="md">
                <Dialog.Header>Add users</Dialog.Header>
                <ValidationForm onSubmit={handleSubmit}>
                    {summary && <AlertBanner type="warning">{summary}</AlertBanner>}
                    <div className={theme.pasteArea}>
                        <textarea
                            className="form-control"
                            rows={3}
                            value={pasteText}
                            disabled={isSaving}
                            placeholder="Paste emails here — separated by new lines, commas or semicolons"
                            onPaste={handlePaste}
                            onChange={(e) => handlePasteTextChange(e.target.value)}
                            onBlur={() => absorbPasteText(pasteText)}
                        />
                        <p className={theme.pasteHint}>
                            Each email becomes a row below that you can edit before adding. Add an access level after an
                            email (<code>user@example.com, Advanced</code>) to override the default.
                        </p>
                        {ignoredNote && <p className={theme.ignoredNote}>{ignoredNote}</p>}
                    </div>

                    {rows.length === 0 ? (
                        <p className={theme.empty}>No users yet — paste a list above or add a row.</p>
                    ) : (
                        <div className={theme.rowList}>
                            {rows.map((row, index) => (
                                <div className={theme.row} key={row.key}>
                                    <ValidatingInput
                                        value={row.email}
                                        required
                                        fieldName={`Email ${index + 1}`}
                                        validations={emailValidations}
                                        placeholder="user@example.com"
                                        className="form-control"
                                        disabled={isSaving}
                                        // Discourage password-manager autofill on this field.
                                        autoComplete="off"
                                        data-1p-ignore
                                        data-lpignore="true"
                                        data-bwignore="true"
                                        data-form-type="other"
                                        onChange={(e) => updateRow(row.key, { email: e.target.value })}
                                    />
                                    <Select
                                        block
                                        inputSize="sm"
                                        options={accessLevelOptions}
                                        value={row.accessLevel}
                                        disabled={isSaving}
                                        onChange={(accessLevel) => updateRow(row.key, { accessLevel })}
                                    />
                                    <Button
                                        size="xs"
                                        variant="tertiary"
                                        icon={faXmark}
                                        title="Remove"
                                        disabled={isSaving}
                                        onClick={() => removeRow(row.key)}
                                    />
                                    {row.error && <p className={theme.rowError}>{row.error}</p>}
                                </div>
                            ))}
                        </div>
                    )}

                    <Button size="sm" variant="tertiary" icon={faPlus} disabled={isSaving} onClick={addEmptyRow}>
                        Add row
                    </Button>

                    <ValidationSummary />
                    <ButtonBar>
                        <ButtonBar.Right>
                            <Button onClick={toggle} variant="tertiary" disabled={isSaving}>
                                Cancel
                            </Button>
                            <Button loading={isSaving} type="submit" variant="primary" disabled={rows.length === 0}>
                                {submitLabel}
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </ValidationForm>
            </Dialog.Content>
        </Dialog>
    );
};

export default MacGyverUsersBulkAddModal;
